import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { environment } from "src/environments/environment";
import {
    ChangeRequest,
    ChangeRequestAttachment,
    ChangeRequestMessage,
    ChangeRequestParticipant,
    CreateChangeRequestDto,
    UserGroup,
} from "../domain/change-request-chat.model";

@Injectable({
    providedIn: "root",
})
export class ChangeRequestChatService {
    constructor(private http: HttpClient) {}

    getOpenDiscussions(): Observable<ChangeRequest[]> {
        return this.http.get<ChangeRequest[]>(
            environment.apiWizardUrl + "api/ChangeRequestChat/GetOpenDiscussions",
            { withCredentials: true }
        );
    }

    getAllChangeRequests(): Observable<ChangeRequest[]> {
        return this.http.get<ChangeRequest[]>(
            environment.apiWizardUrl + "api/ChangeRequestChat/GetAllChangeRequests",
            { withCredentials: true }
        );
    }

    getMyChangeRequests(): Observable<ChangeRequest[]> {
        return this.http.get<ChangeRequest[]>(
            environment.apiWizardUrl + "api/ChangeRequest/GetUserChangeRequests",
            { withCredentials: true }
        );
    }

    getMessages(changeRequestId: number, sinceId?: number): Observable<ChangeRequestMessage[]> {
        let params = new HttpParams().set("id", changeRequestId.toString());
        if (sinceId && sinceId > 0) {
            params = params.set("sinceId", sinceId.toString());
        }
        return this.http.get<ChangeRequestMessage[]>(
            environment.apiWizardUrl + "api/ChangeRequestChat/GetMessages",
            { params, withCredentials: true }
        );
    }

    getChangeRequest(id: number): Observable<ChangeRequest> {
        return this.http.get<ChangeRequest>(
            environment.apiWizardUrl +
                "api/ChangeRequestChat/GetChangeRequest?id=" +
                id,
            { withCredentials: true }
        );
    }

    createChangeRequest(dto: CreateChangeRequestDto): Observable<ChangeRequest> {
        return this.http.post<ChangeRequest>(
            environment.apiWizardUrl + "api/ChangeRequestChat/CreateChangeRequest",
            dto,
            { withCredentials: true }
        );
    }

    addMessage(formData: FormData): Observable<ChangeRequestMessage> {
        return this.http.post<ChangeRequestMessage>(
            environment.apiWizardUrl + "api/ChangeRequestChat/AddMessage",
            formData,
            { withCredentials: true }
        );
    }

    deleteMessage(id: number): Observable<void> {
        return this.http.post<void>(
            environment.apiWizardUrl +
                "api/ChangeRequestChat/DeleteMessage?id=" +
                id,
            null,
            { withCredentials: true }
        );
    }

    downloadFile(messageId: number, fileName: string): Observable<Blob> {
        const params = new HttpParams()
            .set("messageId", messageId.toString())
            .set("fileName", fileName);
        return this.http.get(
            environment.apiWizardUrl + "api/ChangeRequestChat/DownloadFile",
            {
                params,
                withCredentials: true,
                responseType: "blob",
            }
        );
    }

    downloadAttachment(attachment: ChangeRequestAttachment): Observable<Blob> {
        return this.http.get(
            environment.apiWizardUrl + "api/ChangeRequest/DownloadAttachment?id=" + attachment.Id,
            {
                withCredentials: true,
                responseType: "blob",
            }
        );
    }

    uploadFileTemporary(file: File): Observable<{ guid: string }> {
        const formData = new FormData();
        formData.append('file', file);
        return this.http.post<{ guid: string }>(
            environment.apiWizardUrl + "api/ChangeRequest/UploadFileTemporary",
            formData,
            { withCredentials: true }
        );
    }

    hasDiscussionAccess(): Observable<{ hasAccess: boolean; isAdmin: boolean }> {
        return this.http.get<{ hasAccess: boolean; isAdmin: boolean }>(
            environment.apiWizardUrl + "api/ChangeRequest/HasDiscussionAccess",
            { withCredentials: true }
        );
    }

    getCurrentUserContactId(): Observable<{ contactId: number; isAdmin: boolean; isTcMember: boolean }> {
        return this.http.get<{ contactId: number; isAdmin: boolean; isTcMember: boolean }>(
            environment.apiWizardUrl + "api/ChangeRequest/GetCurrentUserContactId",
            { withCredentials: true }
        );
    }

    updateStatus(changeRequestId: number, newStatus: number): Observable<ChangeRequest> {
        return this.http.post<ChangeRequest>(
            environment.apiWizardUrl + "api/ChangeRequestChat/UpdateStatus",
            { changeRequestId, newStatus },
            { withCredentials: true }
        );
    }

    getParticipants(changeRequestId: number): Observable<ChangeRequestParticipant[]> {
        return this.http.get<ChangeRequestParticipant[]>(
            environment.apiWizardUrl + "api/ChangeRequestChat/GetParticipants?changeRequestId=" + changeRequestId,
            { withCredentials: true }
        );
    }

    getAvailableContacts(changeRequestId: number): Observable<ChangeRequestParticipant[]> {
        return this.http.get<ChangeRequestParticipant[]>(
            environment.apiWizardUrl + "api/ChangeRequestChat/GetAvailableContacts?changeRequestId=" + changeRequestId,
            { withCredentials: true }
        );
    }

    getUserGroups(): Observable<UserGroup[]> {
        return this.http.get<UserGroup[]>(
            environment.apiWizardUrl + "api/ChangeRequestChat/GetUserGroups",
            { withCredentials: true }
        );
    }

    addParticipant(changeRequestId: number, contactId: number): Observable<{ skipped: boolean }> {
        return this.http.post<{ skipped: boolean }>(
            environment.apiWizardUrl + "api/ChangeRequestChat/AddParticipant",
            { changeRequestId, contactId },
            { withCredentials: true }
        );
    }

    addUserGroup(changeRequestId: number, userGroupId: number): Observable<{ added: number }> {
        return this.http.post<{ added: number }>(
            environment.apiWizardUrl + "api/ChangeRequestChat/AddUserGroup",
            { changeRequestId, userGroupId },
            { withCredentials: true }
        );
    }

    removeParticipant(changeRequestId: number, contactId: number): Observable<void> {
        return this.http.post<void>(
            environment.apiWizardUrl + "api/ChangeRequestChat/RemoveParticipant",
            { changeRequestId, contactId },
            { withCredentials: true }
        );
    }
}

